<script lang="ts">
  // The catalog: a filterable grid of family cards. Public — the first thing a visitor sees, signed
  // in or not. Filters are the language-neutral category and the family authoring language, which is
  // where the cross-language design shows: filtering by category surfaces the same family in every
  // language it was authored in.

  import { onMount } from "svelte";
  import { auth } from "@/stores/auth.svelte";
  import { catalog, type CatalogScope, type CatalogSort, type CatalogView } from "./catalogStore.svelte";
  import { categoryLabel, languageLabel, nodeLabel } from "./categories";
  import { findLoadedFamily } from "./projectState.svelte";
  import FamilyCard from "./FamilyCard.svelte";
  import FamilyDetailDialog from "./FamilyDetailDialog.svelte";
  import ViewSwitcher, { gridClassFor } from "./ViewSwitcher.svelte";
  import { AppBadge, AppButton, AppInput, AppSelect, AppState } from "@/components/ui";
  import { transport } from "@/transport";
  import type { FamilySummary, PinnedLocation } from "./types";
  import { t, locale } from "@/i18n/index.svelte";
  import type { SupportedLocale } from "@/i18n/index.svelte";

  let openSlug = $state<string | null>(null);

  // The shelves on offer. The public catalog is always there; the other two appear only when they
  // could hold anything — an organization tab for someone in no organization is a promise of an empty
  // screen, and a shelf that is empty by construction is worse than no shelf.
  const shelves = $derived.by(() => {
    const list: { scope: CatalogScope; label: string }[] = [{ scope: "public", label: t("catalog.scopePublic") }];
    if (auth.state.organizationId) {
      list.push({
        scope: "org",
        label: auth.state.organizationName || t("catalog.scopeOrg"),
      });
    }
    if (auth.state.status === "signed-in") {
      list.push({ scope: "favorites", label: t("catalog.scopeFavorites") });
      list.push({ scope: "history", label: t("catalog.scopeHistory") });
      list.push({ scope: "mine", label: t("catalog.scopeMine") });
    }
    return list;
  });

  // Signing out (or being removed from an organization) can retire the shelf currently in view.
  // Falling back rather than showing an empty grid with no explanation.
  function ensureValidScope() {
    if (!shelves.some((s) => s.scope === catalog.state.scope)) catalog.setScope("public");
  }

  const emptyMessage = $derived.by(() => {
    switch (catalog.state.scope) {
      case "org":
        return t("catalog.emptyOrg");
      case "favorites":
        return t("catalog.emptyFavorites");
      case "history":
        return t("catalog.emptyHistory");
      case "mine":
        return t("catalog.emptyMine");
      default:
        return t("catalog.empty");
    }
  });

  // The trail back up. Only the levels actually entered appear, so the root shows just the home
  // crumb and the bar does not reserve space for a depth nobody is at.
  const crumbs = $derived.by(() => {
    const trail: { level: "section" | "category" | "subcategory"; label: string }[] = [];
    const { section, category, subcategory } = catalog.state;

    if (section) trail.push({ level: "section", label: nodeLabel("section", section, locale.value as SupportedLocale, t) });
    if (category) trail.push({ level: "category", label: categoryLabel(category, locale.value as SupportedLocale) });
    if (subcategory) trail.push({ level: "subcategory", label: subcategory });

    return trail;
  });

  const gridClass = $derived(gridClassFor(catalog.state.view));

  const SORTS: CatalogSort[] = ["default", "name", "popular"];

  /**
   * A pin's label: its deepest named level, since that is what distinguishes it. Built at display
   * time rather than stored, so the same pin reads in whatever language is on screen.
   */
  function pinLabel(pin: { section: string | null; category: string | null; subcategory: string | null }): string {
    if (pin.subcategory) return pin.subcategory;
    if (pin.category) return categoryLabel(pin.category, locale.value as SupportedLocale);
    if (pin.section) return nodeLabel("section", pin.section, locale.value as SupportedLocale, t);
    return t("catalog.allFamilies");
  }

  /**
   * Whether this pin is the place currently on screen — what gives a pin tab the same "you are
   * here" underline the shelf tabs have. A pin carries its own scope, so a pin in the organization
   * shelf does not light up while browsing the same category in the public one.
   */
  function isPinnedPlace(pin: PinnedLocation): boolean {
    return (
      pin.scope === catalog.state.scope &&
      (pin.section ?? null) === catalog.state.section &&
      (pin.category ?? null) === catalog.state.category &&
      (pin.subcategory ?? null) === catalog.state.subcategory
    );
  }

  $effect(() => {
    void shelves;
    ensureValidScope();
  });

  // Revit-only, and read once for the life of this view rather than per card or per scroll — see
  // catalogStore.loadProjectState.
  function inProject(family: FamilySummary): boolean {
    return transport.caps.revit && findLoadedFamily(catalog.state.loadedFamilies, family.name) !== undefined;
  }

  onMount(() => {
    void catalog.loadFacets();
    void catalog.loadParameterOptions();
    void catalog.loadProjectState();
    void catalog.reload();
  });

  // Pins are per-account, so they arrive and disappear with the session rather than with the page.
  // This effect's initial run (at mount) also covers the very first load — no separate
  // catalog.loadPinned() call is needed in the onMount block above.
  $effect(() => {
    void auth.state.status;
    void catalog.loadPinned();
  });
</script>

<div class="flex flex-col gap-4">
  <!-- Shelves, then pinned branches, in one strip.
       A shelf switches which library you are looking at; a pin jumps to a place inside one. Two
       different things, so they are told apart by colour — accent for shelves, amber and a star for
       pins — and by a divider, rather than by living in two separate rows. They shared a row in the
       end because they are used the same way and at the same moment: this is the "take me
       somewhere" strip, and a second row of it above the first only made the header taller. -->
  {#if shelves.length > 1 || catalog.state.pinned.length}
    <!-- Wraps rather than scrolls. There is no bound on how many places somebody pins, but a strip
         that scrolls hides tabs behind a gesture nobody thinks to try — and asking for one axis of
         overflow gets you the other for free, which is where the stray vertical scrollbar came
         from. A second line is honest and costs one row. -->
    <div class="flex items-center gap-1 flex-wrap border-b border-line">
      {#each shelves as s (s.scope)}
        <button
          class="text-sm px-3 py-2 -mb-px border-b-2 transition-colors max-w-[14rem] truncate shrink-0 {catalog
            .state.scope === s.scope
            ? 'border-accent text-accent font-medium'
            : 'border-transparent text-ink-muted hover:text-ink'}"
          title={s.label}
          onclick={() => catalog.setScope(s.scope)}
        >
          {s.label}
        </button>
      {/each}

      {#if shelves.length > 1 && catalog.state.pinned.length}
        <span class="w-px h-4 bg-line mx-1.5 shrink-0" aria-hidden="true"></span>
      {/if}

      {#each catalog.state.pinned as pin (pin.id)}
        {@const active = isPinnedPlace(pin)}
        <button
          class="flex items-center gap-1.5 text-sm px-3 py-2 -mb-px border-b-2 transition-colors max-w-[14rem] shrink-0 {active
            ? 'border-warning text-warning font-medium'
            : 'border-transparent text-ink-muted hover:text-warning'}"
          title={pinLabel(pin)}
          onclick={() => catalog.goToPinned(pin)}
        >
          <svg viewBox="0 0 24 24" class="w-3 h-3 shrink-0" fill="currentColor">
            <path d="m12 3 2.6 5.8 6.4.7-4.8 4.3 1.4 6.2L12 17l-5.6 3 1.4-6.2L3 9.5l6.4-.7z" />
          </svg>
          <span class="truncate">{pinLabel(pin)}</span>
        </button>
      {/each}
    </div>
  {/if}

  <!-- Where we are, and the way back. Every crumb is clickable, including home. -->
  <nav class="flex items-center gap-1 text-sm flex-wrap" aria-label={t("catalog.breadcrumb")}>
    <button
      class="p-1 rounded hover:text-accent transition-colors {crumbs.length ? 'text-ink-muted' : 'text-ink'}"
      aria-label={t("catalog.allFamilies")}
      title={t("catalog.allFamilies")}
      onclick={() => catalog.goTo("root")}
    >
      <svg viewBox="0 0 24 24" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.5">
        <path d="m3 10 9-7 9 7v9a2 2 0 0 1-2 2h-4v-6H9v6H5a2 2 0 0 1-2-2z" />
      </svg>
    </button>

    {#each crumbs as c, i (c.level)}
      <span class="text-ink-muted" aria-hidden="true">/</span>
      <button
        class="px-1 rounded transition-colors truncate max-w-[12rem] {i === crumbs.length - 1
          ? 'text-ink font-medium'
          : 'text-ink-muted hover:text-accent'}"
        title={c.label}
        onclick={() => catalog.goTo(c.level)}
      >
        {c.label}
      </button>
    {/each}

    <!-- Pin the branch, not a family in it. Only where there is a branch to pin: at the root the
         home crumb already does that job. -->
    {#if crumbs.length && auth.state.status === "signed-in"}
      <button
        class="p-1 rounded transition-colors {catalog.isCurrentPinned() ? 'text-accent' : 'text-ink-muted hover:text-accent'}"
        aria-pressed={catalog.isCurrentPinned()}
        aria-label={catalog.isCurrentPinned() ? t("catalog.unpin") : t("catalog.pin")}
        title={catalog.isCurrentPinned() ? t("catalog.unpin") : t("catalog.pin")}
        onclick={() => catalog.togglePinned()}
      >
        <svg
          viewBox="0 0 24 24"
          class="w-4 h-4"
          fill={catalog.isCurrentPinned() ? "currentColor" : "none"}
          stroke="currentColor"
          stroke-width="1.6"
        >
          <path d="m12 3 2.6 5.8 6.4.7-4.8 4.3 1.4 6.2L12 17l-5.6 3 1.4-6.2L3 9.5l6.4-.7z" />
        </svg>
      </button>
    {/if}

    <!-- The result count, on the row that already says where you are. It belongs with the location
         rather than with the controls: it is the answer to "what is here", which is what the
         breadcrumb asks — and it had been holding a row of its own open all by itself. -->
    <span class="ml-auto text-xs text-ink-muted tabular-nums shrink-0">
      {t("catalog.results", { count: catalog.state.total })}
    </span>
  </nav>

  <!-- Search, and the controls that decide how the results are shown. -->
  <div class="flex items-center gap-2">
    <AppInput
      type="search"
      size="sm"
      value={catalog.state.search}
      placeholder={t("catalog.searchPlaceholder")}
      aria-label={t("catalog.search")}
      class="flex-1 min-w-0"
      oninput={(event) => catalog.setSearch((event.target as HTMLInputElement).value)}
    />

    <!-- Filters sits with search, sort and density rather than on a row below them. All four decide
         what the grid shows and how; the button had a whole row to itself for no reason other than
         being added later, and in a panel this narrow every row is a row the families are not on.
         Filters live behind a button at all because a permanent row of chips would push the grid off
         the first screen. -->
    <AppButton
      variant={catalog.state.filtersOpen || catalog.activeFilterCount() ? "primary" : "secondary"}
      size="xs"
      aria-expanded={catalog.state.filtersOpen}
      onclick={() => catalog.toggleFilters()}
    >
      <svg viewBox="0 0 24 24" class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="1.6">
        <path d="M3 5h18M6 12h12M10 19h4" />
      </svg>
      {t("catalog.filters")}
      {#if catalog.activeFilterCount()}
        <span class="tabular-nums">({catalog.activeFilterCount()})</span>
      {/if}
    </AppButton>

    <!-- Only when there is something to clear, and next to the button that set it. -->
    {#if catalog.activeFilterCount()}
      <AppButton variant="ghost" size="xs" onclick={() => catalog.clearFilters()}>
        {t("catalog.clearFilters")}
      </AppButton>
    {/if}

    <!-- Sort. Narrow on purpose: it is a setting, not a destination. -->
    <AppSelect
      size="sm"
      value={catalog.state.sort}
      aria-label={t("catalog.sort")}
      class="shrink-0"
      onchange={(event) => catalog.setSort((event.target as HTMLSelectElement).value as CatalogSort)}
    >
      {#each SORTS as sortOption (sortOption)}
        <option value={sortOption}>
          {t(`catalog.sort${sortOption.charAt(0).toUpperCase()}${sortOption.slice(1)}`)}
        </option>
      {/each}
    </AppSelect>

    <ViewSwitcher value={catalog.state.view} onchange={(v) => catalog.setView(v)} />
  </div>

  <!-- The OmniClass branch, when one is selected. On screen rather than inside the filter panel:
       it is reached by clicking a code on a family, so unlike the language chips there is nothing
       on this screen that explains why the grid shrank. A filter you cannot see is a bug report. -->
  {#if catalog.state.omniClass}
    <div class="flex items-center gap-2 flex-wrap">
      <span class="text-xs text-ink-muted">{t("catalog.omniClass")}</span>
      <AppBadge
        tone="accent"
        title={t("catalog.omniClassClear")}
        onclick={() => catalog.setOmniClass(null)}
        class="max-w-[22rem]"
      >
        <span class="font-mono shrink-0">{catalog.state.omniClass}</span>
        {#if catalog.state.omniClassTitle}
          <span class="truncate">{catalog.state.omniClassTitle}</span>
        {/if}
        <span aria-hidden="true">✕</span>
      </AppBadge>
    </div>
  {/if}

  <!-- Folders below the current position. Shown above the grid rather than instead of it: the
       grid keeps listing everything under the current path, so a step down narrows rather than
       replaces, and the root is still a browsable catalog. -->
  {#if catalog.state.nodes.length}
    <div class="flex items-center gap-2 flex-wrap">
      {#each catalog.state.nodes as n (n.key)}
        <AppButton variant="secondary" size="sm" onclick={() => catalog.enter(n.key)}>
          <svg viewBox="0 0 24 24" class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M3 7a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.6.8l1 1.4a2 2 0 0 0 1.6.8H19a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          </svg>
          <span class="truncate max-w-[12rem]">
            {nodeLabel(catalog.state.nodeLevel, n.key, locale.value as SupportedLocale, t)}
          </span>
          <span class="text-xs text-ink-muted tabular-nums">{n.count}</span>
        </AppButton>
      {/each}
    </div>
  {/if}

  <!-- The panel itself. -->
  <!-- Unit system is a fixed pair and always offered, so the panel no longer needs languages or
       parameter options to have something to show — those two stay conditional inside it. -->
  {#if catalog.state.filtersOpen}
    <div class="flex flex-col gap-2 rounded-surface border border-line p-3">
      {#if catalog.state.languages.length}
        <div class="flex items-center gap-2 flex-wrap">
          <span class="text-xs text-ink-muted w-16 shrink-0">{t("catalog.language")}</span>
          {#each catalog.state.languages as l (l)}
            <AppBadge selected={catalog.state.language === l} onclick={() => catalog.setLanguage(l)}>
              {languageLabel(l)}
            </AppBadge>
          {/each}
        </div>
      {/if}

      <!-- A fixed pair, unlike language — no discovery step needed, so this shows regardless of
           what loadFacets found. -->
      <div class="flex items-center gap-2 flex-wrap">
        <span class="text-xs text-ink-muted w-16 shrink-0">{t("catalog.unitSystem")}</span>
        <AppBadge selected={catalog.state.unitSystem === "metric"} onclick={() => catalog.setUnitSystem("metric")}>
          {t("catalog.unitMetric")}
        </AppBadge>
        <AppBadge selected={catalog.state.unitSystem === "imperial"} onclick={() => catalog.setUnitSystem("imperial")}>
          {t("catalog.unitImperial")}
        </AppBadge>
      </div>

      <!-- A select rather than chips: options are sourced from the catalog and can run to a couple
           hundred, where the language row's handful of chips would turn into an unreadable wall. -->
      {#if catalog.state.parameterOptions.length}
        <div class="flex items-center gap-2">
          <span class="text-xs text-ink-muted w-16 shrink-0">{t("catalog.parameter")}</span>
          <AppSelect
            size="xs"
            value={catalog.currentParameterKey()}
            class="flex-1 min-w-0"
            onchange={(event) => catalog.setParameter((event.target as HTMLSelectElement).value)}
          >
            <option value="">{t("catalog.parameterAny")}</option>
            {#each catalog.state.parameterOptions as opt (catalog.parameterOptionKey(opt))}
              <option value={catalog.parameterOptionKey(opt)}>
                {opt.label} ({opt.familyCount})
              </option>
            {/each}
          </AppSelect>
        </div>
      {/if}
    </div>
  {/if}

  <!-- Grid. The three non-grid states go through one component so "loading", "nothing here" and
       "that failed" stop looking like the same grey sentence — only one of them is worth a retry. -->
  {#if catalog.state.loading}
    <AppState kind="loading">{t("common.loading")}</AppState>
  {:else if catalog.state.error}
    <AppState kind="error" retryLabel={t("common.retry")} onretry={() => catalog.reload()}>
      {catalog.state.error}
    </AppState>
  {:else if !catalog.state.items.length}
    <AppState kind="empty">{emptyMessage}</AppState>
  {:else}
    <div class={gridClass}>
      {#each catalog.state.items as f (f.slug)}
        <FamilyCard
          family={f}
          view={catalog.state.view}
          canFavorite={auth.state.status === "signed-in"}
          inProject={inProject(f)}
          onopen={(slug) => (openSlug = slug)}
          onfavorite={(slug) => catalog.toggleFavorite(slug)}
        />
      {/each}
    </div>
  {/if}

  {#if openSlug}
    <FamilyDetailDialog
      slug={openSlug}
      onclose={() => (openSlug = null)}
      onfavoritechanged={catalog.markFavorite}
      ondeleted={(slug) => {
        catalog.removeFamily(slug);
        openSlug = null;
      }}
    />
  {/if}
</div>
